import { useState, useEffect, useCallback, useRef } from "react";
import posthog from "posthog-js";
import { X, Loader2, ChevronLeft, ChevronRight, Wifi } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Mascot } from "@/components/Mascot";
import { CheckInCelebration } from "@/components/CheckInCelebration";
import { useStreakCelebration } from "@/components/streak";
import { PostCheckInReviewPrompt } from "@/components/PostCheckInReviewPrompt";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import type { NoiseLevel } from "@/types/cafe";
import { DialogOverlay } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useWifiSpeedTest as useWifiSpeedTestCtx } from "@/contexts/WifiSpeedTestContext";
import { cafeHasWifi } from "@/lib/amenityGrades";

const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogTitle = DialogPrimitive.Title;

const DialogContent = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
);

interface PostCheckInVibeDialogProps {
  checkInId?: string | null;
  cafeId: string;
  cafeName: string;
  cafeSlug?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCheckInCreated?: (checkInId: string) => void;
  /** If set, a new check-in created here will be marked as a follow-up to this id and won't count as a fresh visit. */
  followupOfCheckInId?: string | null;
}

const vibeOptions = ["Focused", "Chill", "Social", "Chaotic"] as const;

const outletOptions = [
  { value: 1, label: "None" },
  { value: 2, label: "Limited" },
  { value: 3, label: "Some" },
  { value: 5, label: "Plenty" },
];

const crowdednessLevels = [
  { value: 1, label: "Empty" },
  { value: 2, label: "Light" },
  { value: 3, label: "Normal" },
  { value: 4, label: "Busy" },
  { value: 5, label: "Packed" },
];

// Display chips → DB enum value (noise_level enum: quiet | moderate | lively | loud)
const noiseOptions: { label: string; value: NoiseLevel }[] = [
  { label: "Quiet", value: "quiet" },
  { label: "Moderate", value: "moderate" },
  { label: "Loud", value: "loud" },
];

const TOTAL_STEPS = 4;

export function PostCheckInVibeDialog({
  checkInId,
  cafeId,
  cafeName,
  cafeSlug,
  open,
  onOpenChange,
  onCheckInCreated,
  followupOfCheckInId,
}: PostCheckInVibeDialogProps) {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { openWifiTest } = useWifiSpeedTestCtx();
  const { showQueuedCelebration } = useStreakCelebration();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [resolvedCheckInId, setResolvedCheckInId] = useState<string | null>(checkInId ?? null);
  const [vibeSubmitted, setVibeSubmitted] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const directionRef = useRef<"forward" | "back">("forward");
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch wifi presence so we can hide the speed-test CTA for cafes without WiFi.
  const [wifiInfo, setWifiInfo] = useState<{ has_wifi: boolean | null; wifi_type: string | null } | null>(null);
  useEffect(() => {
    if (!open || !cafeId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("cafe_amenities")
        .select("has_wifi, wifi_type")
        .eq("cafe_id", cafeId);
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setWifiInfo(row as any);
    })();
    return () => { cancelled = true; };
  }, [open, cafeId]);
  const showWifiTestCta = cafeHasWifi(wifiInfo);

  useEffect(() => {
    setResolvedCheckInId(checkInId ?? null);
  }, [checkInId]);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setShowConfirmation(false);
      setShowSkipConfirm(false);
      setVibeSubmitted(false);
      directionRef.current = "forward";
      if (autoAdvanceTimerRef.current !== null) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current !== null) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [currentStep, open]);

  // Form state
  const [vibeTags, setVibeTags] = useState<string[]>([]);
  const [outlets, setOutlets] = useState<number | null>(null);
  const [crowdedness, setCrowdedness] = useState<number | null>(null);
  const [noiseLevel, setNoiseLevel] = useState<NoiseLevel | null>(null);

  const hasAnyData = vibeTags.length > 0 || outlets !== null || crowdedness !== null || noiseLevel !== null;

  const goNext = useCallback(() => {
    if (currentStep < TOTAL_STEPS - 1) {
      directionRef.current = "forward";
      setCurrentStep((s) => s + 1);
    }
  }, [currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      directionRef.current = "back";
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const autoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current !== null) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      goNext();
    }, 800);
  }, [goNext]);

  const scheduleWifiFollowup = (checkInIdForPush: string) => {
    if (!currentUser?.id) return;
    const sendAfter = new Date(Date.now() + 10 * 60 * 1000).toISOString();
    supabase.functions
      .invoke("send-push-notification", {
        body: {
          mode: "wifi_followup",
          user_id: currentUser.id,
          cafe_id: cafeId,
          cafe_slug: cafeSlug,
          cafe_name: cafeName,
          check_in_id: checkInIdForPush,
          send_after: sendAfter,
          title: `You're at ${cafeName}`,
          message: `Got a minute to test their WiFi speed?`,
        },
      })
      .catch((e) => console.warn("[PostCheckInVibeDialog] wifi followup push failed:", e));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let targetCheckInId = checkInId;
      // Track whether this submission minted a fresh row (vs. attached to an
      // existing check-in or reused a same-hour sibling on 23505).
      let mintedNewRow = false;

      if (!targetCheckInId) {
        const userId = currentUser?.id;
        if (!userId) throw new Error("Not authenticated");
        const insertPayload = {
          cafe_id: cafeId,
          user_id: userId,
          reflection_completed: true,
          source: "vibe_report",
          source_recorded_at: new Date().toISOString(),
          ...(followupOfCheckInId ? { followup_checkin_id: followupOfCheckInId } : {}),
        } as any;
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const { data: newCheckIn, error: insertError } = await supabase
          .from("check_ins")
          .insert(insertPayload)
          .select("id")
          .single();
        if (insertError) {
          if ((insertError as any).code === "23505" && !followupOfCheckInId) {
            // Someone already checked in this UTC hour — reuse it and enrich.
            const { data: sibling } = await supabase
              .from("check_ins")
              .select("id")
              .eq("cafe_id", cafeId)
              .eq("user_id", userId)
              .gte("checked_in_at", oneHourAgo)
              .order("checked_in_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (!sibling) throw insertError;
            targetCheckInId = sibling.id;
          } else {
            throw insertError;
          }
        } else {
          targetCheckInId = newCheckIn.id;
          mintedNewRow = true;
        }
        setResolvedCheckInId(targetCheckInId);
        onCheckInCreated?.(targetCheckInId);
      }

      const updatePayload: Record<string, any> = {
        outlets_available: outlets,
        noise_level: noiseLevel,
        crowdedness: crowdedness,
        vibe_tag: vibeTags[0] ?? null,
        vibe_tags: vibeTags,
        is_complete: true,
        reflection_completed: true,
      };

      const { error } = await supabase
        .from("check_ins")
        .update(updatePayload)
        .eq("id", targetCheckInId);

      if (error) throw error;

      // Clear any other orphan incomplete rows for this user+café in the last 24h
      // so the IncompleteVibePromptProvider can't re-surface them.
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      if (currentUser?.id) {
        const { error: cleanupError } = await supabase
          .from("check_ins")
          .update({ is_complete: true })
          .eq("user_id", currentUser.id)
          .eq("cafe_id", cafeId)
          .eq("is_complete", false)
          .gt("checked_in_at", twentyFourHoursAgo);
        if (cleanupError) {
          console.warn("[PostCheckInVibeDialog] orphan cleanup failed:", cleanupError);
        }
      }
      queryClient.invalidateQueries({ queryKey: ["incomplete-vibe-checkin", currentUser?.id] });

      // Suppress proximity prompts for the rest of this session
      try { sessionStorage.setItem("proximity-prompt-resolved", Date.now().toString()); } catch {}

      // Analytics: only emit `checkin_submitted` when this dialog itself
      // resolved the row (fresh mint or 23505 sibling reuse). In the
      // attach-to-existing case (checkInId prop was set) the upstream
      // check-in flow already fired `checkin_submitted`, so re-emitting here
      // would double-count the same check_in_id with two `source` values.
      // `vibe_report_submitted` always fires and carries the resolved id.
      const resolvedHere = !checkInId;
      if (resolvedHere) {
        try {
          posthog.capture("checkin_submitted", {
            cafe_id: cafeId,
            cafe_name: cafeName,
            source: "vibe_report",
            minted_new_row: mintedNewRow,
            is_followup: !!followupOfCheckInId,
            check_in_id: targetCheckInId,
          });
        } catch { /* ignore */ }
      }
      try {
        posthog.capture("vibe_report_submitted", {
          cafe_id: cafeId,
          cafe_name: cafeName,
          check_in_id: targetCheckInId,
          minted_new_row: mintedNewRow,
        });
      } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: ["cafe-checkins", cafeId] });
      queryClient.invalidateQueries({ queryKey: ["recent-cafe-conditions"] });
      queryClient.invalidateQueries({ queryKey: ["todayCheckIns", cafeId] });
      // Standalone "report current conditions" path (no incoming checkInId)
      // minted a fresh check-in row above, which can flip contribution / today
      // / user-checkins caches. Invalidate those keys too.
      if (resolvedHere && currentUser?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-checked-in-today", cafeId, currentUser.id] });
        queryClient.invalidateQueries({ queryKey: ["user-cafe-contributions", cafeId, currentUser.id] });
        queryClient.invalidateQueries({ queryKey: ["user-checkins", currentUser.id] });
      }

      // Schedule the 10-min WiFi follow-up push
      scheduleWifiFollowup(targetCheckInId!);

      toast({
        title: "Thanks for the vibe check!",
        description: "Your report helps others find great spots.",
      });

      if (currentUser?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-streak", currentUser.id] });
        queryClient.invalidateQueries({ queryKey: ["user-month-checkins", currentUser.id] });
      }

      setVibeSubmitted(true);
      // Show in-modal confirmation instead of immediately closing
      setShowConfirmation(true);
    } catch (error: any) {
      console.error("Error saving vibe data:", error);
      toast({
        title: "Couldn't save your report",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (checkInId) {
      try {
        await supabase.from("check_ins").update({ is_complete: true }).eq("id", checkInId);
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        if (currentUser?.id) {
          const { error: cleanupError } = await supabase
            .from("check_ins")
            .update({ is_complete: true })
            .eq("user_id", currentUser.id)
            .eq("cafe_id", cafeId)
            .eq("is_complete", false)
            .gt("checked_in_at", twentyFourHoursAgo);
          if (cleanupError) {
            console.warn("[PostCheckInVibeDialog] orphan cleanup failed on skip:", cleanupError);
          }
        }
        queryClient.invalidateQueries({ queryKey: ["incomplete-vibe-checkin", currentUser?.id] });
        try { sessionStorage.setItem("proximity-prompt-resolved", Date.now().toString()); } catch {}
        queryClient.invalidateQueries({ queryKey: ["cafe-checkins", cafeId] });
        if (currentUser?.id) {
          queryClient.invalidateQueries({ queryKey: ["user-streak", currentUser.id] });
          queryClient.invalidateQueries({ queryKey: ["user-month-checkins", currentUser.id] });
        }
      } catch (e) {
        console.error("[PostCheckInVibeDialog] Error marking complete on skip:", e);
      }
    }
    onOpenChange(false);
    if (checkInId) {
      setTimeout(() => setShowCelebration(true), 300);
    }
  };

  const handleConfirmationDismiss = () => {
    onOpenChange(false);
    setTimeout(() => setShowCelebration(true), 300);
  };

  const handleConfirmationTestWifi = () => {
    onOpenChange(false);
    // Open WiFi test instead of celebration chain
    setTimeout(() => {
      openWifiTest({
        cafeId,
        cafeName,
        checkInId: resolvedCheckInId,
        source: "checkin_confirm",
      });
    }, 250);
  };

  const handleCelebrationDone = useCallback(() => {
    setShowCelebration(false);
    showQueuedCelebration();
    setTimeout(() => setShowReviewPrompt(true), 300);
  }, [showQueuedCelebration]);

  useEffect(() => {
    if (open) {
      console.log("[PostCheckInVibeDialog] Dialog opened for check-in:", checkInId);
    }
  }, [open, checkInId]);

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const animClass =
    directionRef.current === "forward"
      ? "animate-in fade-in-0 slide-in-from-right-4 duration-200"
      : "animate-in fade-in-0 slide-in-from-left-4 duration-200";

  const steps: { question: string; subtext: string }[] = [
    {
      question: "What's the vibe right now?",
      subtext: "How the room feels at this moment.",
    },
    {
      question: "How are outlets right now?",
      subtext: "Based on what you're seeing at this moment.",
    },
    {
      question: "How crowded is it right now?",
      subtext: "What you're seeing at this moment.",
    },
    {
      question: "How's the noise level right now?",
      subtext: "What you're hearing at this moment.",
    },
  ];

  // Defensive: never index past the array. A bad currentStep would otherwise
  // throw "Cannot read properties of undefined (reading 'question')" during
  // render and crash the whole flow to the ErrorBoundary "Oops" screen.
  const activeStep = steps[currentStep] ?? steps[0];

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            if (showConfirmation) {
              handleConfirmationDismiss();
            } else {
              handleSkip();
            }
          }
        }}
      >
        <DialogContent className="max-w-sm p-0 gap-0 max-h-[85vh] supports-[height:1dvh]:max-h-[85dvh] overflow-y-auto border-0 shadow-xl">
          <VisuallyHidden>
            <DialogTitle>How's the vibe?</DialogTitle>
          </VisuallyHidden>

          {!showConfirmation ? (
            <>
              {/* Header */}
              <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 pb-3">
                <button
                  onClick={() => setShowSkipConfirm(true)}
                  className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-background/50 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>

                {showSkipConfirm && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center bg-background/95 backdrop-blur-sm p-4 pt-10 rounded-t-lg">
                    <div className="w-full max-w-xs text-center space-y-3">
                      <p className="font-display text-base font-semibold text-foreground">
                        Skip vibe report?
                      </p>
                      <p className="text-xs text-muted-foreground">
                        We'll remind you later.
                      </p>
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setShowSkipConfirm(false)}
                        >
                          Go back
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setShowSkipConfirm(false);
                            handleSkip();
                          }}
                        >
                          Skip
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Mascot mood="happy" size="sm" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      How's the vibe?
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                      Step {currentStep + 1} of {TOTAL_STEPS}
                    </p>
                  </div>
                </div>

                <div className="mt-3">
                  <Progress value={((currentStep + 1) / TOTAL_STEPS) * 100} className="h-1.5" />
                </div>
              </div>

              {/* Step content */}
              <div className="p-4 min-h-[180px] flex flex-col justify-center" key={currentStep}>
                <div className={animClass}>
                  <label className="text-xl font-semibold mb-1 block">
                    {activeStep.question}
                  </label>
                  <p className="text-xs text-muted-foreground mb-4">{activeStep.subtext}</p>

                  {/* Step 0: Vibe */}
                  {currentStep === 0 && (
                    <div className="flex flex-wrap gap-2">
                      {vibeOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            setVibeTags((prev) =>
                              prev.includes(option)
                                ? prev.filter((t) => t !== option)
                                : [...prev, option]
                            );
                          }}
                          className={cn(
                            "flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all border-2 min-w-[calc(50%-0.25rem)]",
                            vibeTags.includes(option)
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-transparent bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                          )}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 1: Outlets */}
                  {currentStep === 1 && (
                    <div className="flex gap-2">
                      {outletOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setOutlets(option.value);
                            autoAdvance();
                          }}
                          className={cn(
                            "flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all border-2",
                            outlets === option.value
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-transparent bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 2: Crowdedness */}
                  {currentStep === 2 && (
                    <div className="flex gap-1">
                      {crowdednessLevels.map((level) => (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() => {
                            setCrowdedness(level.value);
                            autoAdvance();
                          }}
                          className={cn(
                            "flex-1 py-2.5 px-1 rounded-lg text-[11px] font-medium transition-all border-2",
                            crowdedness === level.value
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-transparent bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                          )}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 3: Noise (tap chips) */}
                  {currentStep === 3 && (
                    <div className="flex gap-2">
                      {noiseOptions.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => setNoiseLevel(option.value)}
                          className={cn(
                            "flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all border-2",
                            noiseLevel === option.value
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-transparent bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 pt-3 space-y-2 bg-background border-t">
                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <Button variant="outline" size="sm" onClick={goBack} className="shrink-0">
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back
                    </Button>
                  )}
                  <Button
                    onClick={isLastStep ? handleSubmit : goNext}
                    disabled={isSubmitting || (currentStep === 0 && vibeTags.length === 0)}
                    className="flex-1"
                    size="sm"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : isLastStep ? (
                      hasAnyData ? "Submit Vibe Report" : "Share your vibe"
                    ) : (
                      <>
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                  className="w-full text-muted-foreground hover:text-foreground"
                  size="sm"
                >
                  Skip for now
                </Button>
              </div>
            </>
          ) : (
            /* Confirmation screen */
            <>
              <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 pb-3">
                <button
                  onClick={handleConfirmationDismiss}
                  className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-background/50 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
                <div className="flex flex-col items-center gap-2 pt-2 pb-1 text-center">
                  <Mascot mood="happy" size="md" />
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    You're all set.
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Thanks for keeping the community informed.
                  </p>
                </div>
              </div>

              <div className="p-4 space-y-3">
                {showWifiTestCta ? (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                    <p className="text-sm text-foreground/90">
                      While you're here — want to test the WiFi speed?
                    </p>
                    <Button onClick={handleConfirmationTestWifi} className="w-full" size="sm">
                      <Wifi className="h-4 w-4 mr-2" />
                      Test WiFi →
                    </Button>
                    <button
                      type="button"
                      onClick={handleConfirmationDismiss}
                      className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Maybe later
                    </button>
                  </div>
                ) : (
                  <Button onClick={handleConfirmationDismiss} className="w-full" size="sm">
                    Done
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CheckInCelebration
        cafeId={cafeId}
        cafeName={cafeName}
        open={showCelebration}
        onDone={handleCelebrationDone}
        vibeSubmitted={vibeSubmitted}
        checkInId={resolvedCheckInId}
      />

      {resolvedCheckInId && (
        <PostCheckInReviewPrompt
          cafeId={cafeId}
          cafeName={cafeName}
          checkInId={resolvedCheckInId}
          open={showReviewPrompt}
          onOpenChange={setShowReviewPrompt}
        />
      )}
    </>
  );
}
